import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Stack,
  TextField,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import AddLinkIcon from '@mui/icons-material/AddLink';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getAuth } from 'firebase/auth';
import type { Application } from '../../types/application';
import {
  INFLOW_CHANNEL_VALUES,
  INFLOW_LABELS,
  getInflowLabel,
  getInflowColor,
  normalizeInflowSource,
} from '../../../../constants/inflowChannels';
import { PUBLIC_SITE_ORIGIN } from '../../../../constants/siteConfig';
import {
  createInflowLink,
  deleteInflowLink,
  fetchInflowLinks,
  type InflowLink,
} from '../../../../services/inflowLinkService';

/** 링크 생성기에서 선택 가능한 채널 (direct/referral은 자동 판별 값이라 제외) */
const CHANNEL_OPTIONS = INFLOW_CHANNEL_VALUES.filter(
  (v) => v !== 'direct' && v !== 'referral',
);

interface Props {
  /** 링크별 누적 신청수 계산용 (전체 신청 목록) */
  applications: Application[];
}

/**
 * 📈 추적 링크 관리 (생성 + 저장 리스트 + 링크별 성과)
 * - 생성한 링크는 Firestore(inflow_links)에 저장되어 관리자 전원이 공유
 * - 각 링크로 접수된 누적 신청수를 함께 표시
 */
const InflowLinkManager = ({ applications }: Props) => {
  // ── 생성 폼 상태
  const [source, setSource] = useState<string>('gmarket');
  const [medium, setMedium] = useState('banner');
  const [campaign, setCampaign] = useState('');
  const [target, setTarget] = useState<'b2b' | 'b2c'>('b2b');
  const [saving, setSaving] = useState(false);

  // ── 저장된 링크 리스트 상태
  const [links, setLinks] = useState<InflowLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [linksError, setLinksError] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const notify = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const loadLinks = useCallback(async () => {
    setLinksLoading(true);
    setLinksError(false);
    try {
      setLinks(await fetchInflowLinks());
    } catch (error) {
      console.error('추적 링크 목록 조회 실패:', error);
      setLinksError(true);
    } finally {
      setLinksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // ── 생성 미리보기 URL (실제 배포 도메인 기준)
  const generatedUrl = useMemo(() => {
    const path = target === 'b2c' ? '/dawontrade-in' : '/';
    const url = new URL(path, PUBLIC_SITE_ORIGIN);
    const normalized = source.trim() ? normalizeInflowSource(source) : '';
    if (normalized) url.searchParams.set('utm_source', normalized);
    if (medium.trim()) url.searchParams.set('utm_medium', medium.trim());
    if (campaign.trim()) url.searchParams.set('utm_campaign', campaign.trim());
    return url.toString();
  }, [source, medium, campaign, target]);

  // ── 링크별 누적 신청수 (채널 일치 + 캠페인 지정 시 캠페인까지 일치)
  const linkCounts = useMemo(() => {
    const counts = new Map<string, number>();
    links.forEach((link) => {
      counts.set(
        link.id,
        applications.filter(
          (app) =>
            app.inflow?.source === link.source &&
            (!link.campaign || app.inflow?.campaign === link.campaign),
        ).length,
      );
    });
    return counts;
  }, [links, applications]);

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      notify('추적 링크가 복사되었습니다!');
    } catch (error) {
      console.error('링크 복사 실패:', error);
      notify('복사에 실패했습니다. 링크를 직접 선택해 복사해주세요.', 'error');
    }
  };

  const handleSave = async () => {
    const normalized = source.trim() ? normalizeInflowSource(source) : '';
    if (!normalized) {
      notify('채널(utm_source)을 입력해주세요.', 'error');
      return;
    }
    const duplicate = links.find(
      (l) =>
        l.source === normalized &&
        l.campaign === campaign.trim() &&
        l.target === target,
    );
    if (duplicate) {
      notify('동일한 채널/캠페인/대상의 링크가 이미 저장되어 있습니다.', 'error');
      return;
    }

    setSaving(true);
    try {
      await createInflowLink({
        source: normalized,
        medium: medium.trim(),
        campaign: campaign.trim(),
        target,
        url: generatedUrl,
        created_by: getAuth().currentUser?.email ?? '',
      });
      await loadLinks();
      notify('링크가 저장되었습니다. 오픈마켓 배너에 적용해주세요!');
    } catch (error) {
      console.error('추적 링크 저장 실패:', error);
      notify('링크 저장에 실패했습니다. (Firestore inflow_links 권한 확인 필요)', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (link: InflowLink) => {
    const confirmed = window.confirm(
      `'${getInflowLabel(link.source)}${link.campaign ? ` / ${link.campaign}` : ''}' 링크를 삭제하시겠습니까?\n(이미 수집된 신청 데이터는 유지됩니다)`,
    );
    if (!confirmed) return;
    try {
      await deleteInflowLink(link.id);
      setLinks((prev) => prev.filter((l) => l.id !== link.id));
      notify('링크가 삭제되었습니다.');
    } catch (error) {
      console.error('추적 링크 삭제 실패:', error);
      notify('링크 삭제에 실패했습니다.', 'error');
    }
  };

  const formatDate = (ts: { seconds: number } | null) =>
    ts?.seconds
      ? new Date(ts.seconds * 1000).toLocaleDateString('ko-KR', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
        })
      : '-';

  return (
    <Paper elevation={0} sx={{
      p: { xs: 2, md: 3 }, borderRadius: 3,
      border: '1px solid #e5e7eb',
    }}>
      <Typography fontWeight={700} sx={{ fontSize: '0.95rem', color: '#0f172a', mb: 0.5 }}>
        추적 링크 관리
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2.5, display: 'block' }}>
        링크를 생성·저장하고 오픈마켓 배너의 랜딩 URL로 적용하면, 그 배너를 타고 온 신청이 자동 집계됩니다
      </Typography>

      <Grid container spacing={3}>
        {/* ── 좌: 링크 생성 폼 */}
        <Grid item xs={12} md={5}>
          <Stack spacing={2} sx={{
            p: 2, borderRadius: 2.5,
            bgcolor: '#f8fafc', border: '1px solid #eef2f7',
            height: '100%',
            '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
          }}>
            <Autocomplete
              freeSolo
              size="small"
              options={CHANNEL_OPTIONS as readonly string[]}
              inputValue={source}
              onInputChange={(_, value) => setSource(value)}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box sx={{
                      width: 10, height: 10, borderRadius: '50%',
                      bgcolor: getInflowColor(option), flexShrink: 0,
                    }} />
                    <Typography variant="body2">
                      {INFLOW_LABELS[option] ?? option}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({option})
                      </Typography>
                    </Typography>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="채널 (utm_source)"
                  placeholder="예: gmarket — 새 채널은 영문으로 직접 입력"
                />
              )}
            />

            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                label="매체 (utm_medium)"
                value={medium}
                onChange={(e) => setMedium(e.target.value)}
                placeholder="banner"
              />
              <TextField
                size="small"
                fullWidth
                label="캠페인 (선택)"
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                placeholder="예: 2026summer"
              />
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                랜딩 페이지
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={target}
                onChange={(_, value) => value && setTarget(value)}
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 2, fontWeight: 600, fontSize: 13, textTransform: 'none',
                  },
                }}
              >
                <ToggleButton value="b2b">B2B 메인 (/)</ToggleButton>
                <ToggleButton value="b2c">B2C 메인 (/dawontrade-in)</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TextField
              size="small"
              fullWidth
              value={generatedUrl}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                sx: { fontSize: 13, fontFamily: 'monospace', bgcolor: '#f8fafc' },
              }}
            />

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                onClick={() => copyToClipboard(generatedUrl)}
                sx={{ fontWeight: 600, flex: 1 }}
              >
                복사
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddLinkIcon sx={{ fontSize: 16 }} />}
                onClick={handleSave}
                disabled={saving}
                sx={{ fontWeight: 600, flex: 2, whiteSpace: 'nowrap' }}
              >
                링크 저장
              </Button>
            </Stack>
          </Stack>
        </Grid>

        {/* ── 우: 저장된 링크 리스트 + 성과 */}
        <Grid item xs={12} md={7}>
          <Box sx={{
            border: '1px solid #f0f0f0', borderRadius: 2,
            overflow: 'hidden', height: '100%',
          }}>
            <Box sx={{
              px: 2, py: 1.2, bgcolor: '#f9fafc',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                저장된 링크 ({links.length})
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                신청수 = 해당 채널(캠페인) 유입 누적
              </Typography>
            </Box>

            {linksLoading ? (
              <Box textAlign="center" py={4}>
                <CircularProgress size={22} />
              </Box>
            ) : linksError ? (
              <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
                링크 목록을 불러오지 못했습니다. (Firestore inflow_links 권한 확인 필요)
              </Alert>
            ) : links.length === 0 ? (
              <Box textAlign="center" py={4} px={2}>
                <Typography variant="body2" color="text.secondary">
                  아직 저장된 링크가 없습니다.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  왼쪽에서 채널을 골라 첫 추적 링크를 만들어보세요.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="저장된 추적 링크 목록">
                  <TableHead>
                    <TableRow>
                      {['채널', '캠페인 / 매체', '대상', '신청수', '등록', ''].map((label) => (
                        <TableCell
                          key={label}
                          align={label === '신청수' ? 'right' : 'left'}
                          sx={{ fontWeight: 700, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', py: 1 }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {links.map((link) => {
                      const color = getInflowColor(link.source);
                      const count = linkCounts.get(link.id) ?? 0;
                      return (
                        <TableRow key={link.id} hover>
                          <TableCell sx={{ py: 1 }}>
                            <Chip
                              label={getInflowLabel(link.source)}
                              size="small"
                              sx={{
                                fontWeight: 700, fontSize: 12, height: 22,
                                color, bgcolor: `${color}14`,
                                border: `1px solid ${color}30`,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155' }}>
                              {link.campaign || '-'}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                              {link.medium || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Chip
                              label={link.target === 'b2c' ? 'B2C' : 'B2B'}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11, height: 20, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1 }}>
                            <Typography sx={{
                              fontSize: 13, fontWeight: 800,
                              color: count > 0 ? '#1976d2' : '#94a3b8',
                            }}>
                              {count.toLocaleString()}건
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1, fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                            {formatDate(link.created_at)}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1, whiteSpace: 'nowrap' }}>
                            <Tooltip title={link.url} arrow>
                              <IconButton size="small" onClick={() => copyToClipboard(link.url)}>
                                <ContentCopyIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="삭제" arrow>
                              <IconButton size="small" onClick={() => handleDelete(link)}>
                                <DeleteOutlineIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default InflowLinkManager;
